import React, { ChangeEvent, useState, useEffect } from 'react';
import {
  Box,
  Flex,
  Input,
  MagnifyingGlassIcon,
  PrimaryButton,
  defaultColors,
} from '@lifechurch/react-ion';
import { useRouter } from 'next/router';
import { GetServerSideProps, NextPage } from 'next';
import TableHeader from '@components/Users/TableHeader';
import { SUBSCRIBERS } from '@lib/queries';
import { useQuery } from '@apollo/client';
import { PageHeader } from '@components/PageHeader';
import RelayPagination from '@components/RelayPagination';
import ExportUsersButton from '@components/Users/ExportUsersButton';
import { usePageContext } from '@lib/pageContext';
import {
  CurrentOrganizationOnboardingQuery,
  CurrentOrganizationOnboardingQueryVariables,
  SubscribersQuery,
} from '@generated/types';
import { OnboardingStepKeys } from '@components/Onboarding/ChecklistInProgress/ChecklistItems/ChecklistItem';
import InfoValuePropModal from '@components/Onboarding/Modals/InfoValuePropModal';
import { CURRENT_ORGANIZATION_ONBOARDING } from '@lib/queries';
import { TableBody } from '@components/Users/TableBody';
import { LoadingScreen } from '@components/LoadingScreen';
import { useDebounce } from '@lib/useDebounce';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { DEFAULT_LOCALE } from '@utils/constants';
import { useTranslation } from 'react-i18next';

const defaultLimit = 25;

interface PageProps {
  backgroundColor: string;
}

const paginationStyles = {
  justifyContent: 'center',
  marginTop: '24px',
};

const Users: NextPage<PageProps> = () => {
  const router = useRouter();
  const { scrollToTop } = usePageContext();
  const { t } = useTranslation(['users', 'common']);

  const handleAddUser = (): Promise<boolean> => router.push('/user/new');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);

  const debouncedSearch = useDebounce(search, 500);

  const { data, refetch } = useQuery<SubscribersQuery>(SUBSCRIBERS, {
    fetchPolicy: 'network-only',
    variables: {
      first: defaultLimit,
      filter: {
        search: debouncedSearch,
      },
    },
  });

  const { data: onboardingData } = useQuery<
    CurrentOrganizationOnboardingQuery,
    CurrentOrganizationOnboardingQueryVariables
  >(CURRENT_ORGANIZATION_ONBOARDING);

  const inviteTeamStep = onboardingData?.currentOrganization?.onboardingSteps?.find(
    step => step.key === OnboardingStepKeys.INVITE
  );
  // If the step isn't present, assume it is complete
  const onboardingStepComplete = (inviteTeamStep?.completed || inviteTeamStep?.skipped) ?? true;
  const [showValueProp, setShowValueProp] = useState(!onboardingStepComplete);
  const onValuePropDismiss = (): void => setShowValueProp(false);

  useEffect(() => {
    setShowValueProp(!onboardingStepComplete);
  }, [onboardingStepComplete]);

  const pageInfo = data?.currentOrganization?.subscribers?.pageInfo;

  const onChange = async (e: ChangeEvent<HTMLInputElement>): Promise<void> => {
    const { value } = e.currentTarget;
    setSearch(value);
    setPage(1);
  };

  const handleNext = async (): Promise<void> => {
    await refetch({
      first: defaultLimit,
      last: null,
      before: null,
      after: pageInfo?.endCursor,
    });
    scrollToTop();
    setPage(prev => prev + 1);
  };

  const handlePrev = async (): Promise<void> => {
    await refetch({
      first: null,
      last: defaultLimit,
      before: pageInfo?.startCursor,
      after: null,
    });
    scrollToTop();
    setPage(prev => prev - 1);
  };

  return (
    <>
      <PageHeader title={t('users')} />
      {!data ? (
        <LoadingScreen />
      ) : (
        <>
          <InfoValuePropModal
            onDismiss={onValuePropDismiss}
            open={showValueProp}
            imageUrl='https://chop.imgix.net/static/admin/info_users.png'
            infoText={t('users_description')}
          />
          <Flex marginBlockEnd={6}>
            <Box width='250px' position='relative'>
              <Box position='absolute' top='10px' marginInlineStart={4} zIndex={1}>
                <MagnifyingGlassIcon />
              </Box>
              <Input
                placeholder={t('search')}
                paddingInlineStart={7}
                value={search}
                onChange={onChange}
              />
            </Box>
            <ExportUsersButton />
            <PrimaryButton onClick={handleAddUser} pulsing={!onboardingStepComplete}>
              {t('invite_user')}
            </PrimaryButton>
          </Flex>
          <Box>
            <TableHeader />
            <TableBody data={data} />
          </Box>
          <RelayPagination
            totalCount={data?.currentOrganization?.subscribers?.totalCount ?? 0}
            currentPage={page}
            currentPageSize={data?.currentOrganization?.subscribers?.edges?.length ?? 0}
            pageSize={defaultLimit}
            pageInfo={pageInfo}
            onNext={handleNext}
            onPrev={handlePrev}
            styles={paginationStyles}
          />
        </>
      )}
    </>
  );
};

export const getServerSideProps: GetServerSideProps = async ctx => ({
  props: {
    backgroundColor: defaultColors.gray5,
    ...(await serverSideTranslations(ctx.locale || DEFAULT_LOCALE, ['common', 'users'])),
  },
});

export default Users;
